import React from "react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  client,
  TableSettingsDto,
  UpdateTableSettingsDto,
  UserDetailsDto,
  UserSettingsDto,
} from "@/client";

/**
 * Where a table's saved layout lives (ID-6897): the caller's own `userSettings` on GET /users/me,
 * written through PATCH / DELETE /users/table-settings/:tableKey. The table code only talks to this
 * adapter, so the storage can be swapped -- tests inject an in-memory one through the provider.
 */
export type TableSettingsEntry = {
  /** The user the layout belongs to; changes when someone else signs in. */
  userId?: number;
  /** undefined until /me is known, and when nothing is stored for the table. */
  value?: TableSettingsDto;
};

export type TableSettingsAdapter = {
  /** A hook: the saved layout for `tableKey`. */
  useEntry: (tableKey?: string) => TableSettingsEntry;
  /** Per-field merge (an omitted field keeps, null or [] clears). Resolves to the entry as stored after the merge. */
  save: (
    tableKey: string,
    body: UpdateTableSettingsDto,
  ) => Promise<TableSettingsDto | undefined>;
  /** Reset to default: the entry is removed, never replaced by a stored copy of the defaults. */
  reset: (tableKey: string) => Promise<void>;
};

export const findTableSettings = (
  settings: UserSettingsDto | undefined,
  tableKey: string,
): TableSettingsDto | undefined =>
  settings?.tablesSettings?.find((entry) => entry.key === tableKey)?.value;

const NO_ENTRY: TableSettingsEntry = {};

// `staleTime: Infinity`: this observer never adds a /me refetch of its own. The dashboard's other
// /me observers keep the data fresh, and a write patches the cache from the response.
const useServerEntry = (tableKey?: string): TableSettingsEntry => {
  const select = React.useCallback(
    (me: UserDetailsDto): TableSettingsEntry => ({
      userId: me.id,
      value: tableKey
        ? findTableSettings(me.userSettings, tableKey)
        : undefined,
    }),
    [tableKey],
  );
  const { data } = useQuery({
    ...client.users.me.asQueryOptions(),
    enabled: !!tableKey,
    staleTime: Infinity,
    select,
  });
  return data ?? NO_ENTRY;
};

export const createServerTableSettingsAdapter = (
  queryClient: QueryClient,
): TableSettingsAdapter => {
  const meKey = client.users.me.queryKey;
  const apply = (userSettings: UserSettingsDto) =>
    queryClient.setQueryData<UserDetailsDto>(
      meKey,
      (me) => me && { ...me, userSettings },
    );
  // A /me refetch (window focus, a mount elsewhere) that started before the write and lands after
  // it would put the pre-write userSettings back into the cache, so in-flight fetches are cancelled
  // on both sides of the request -- but only while there is cached data to keep showing: right
  // after the cache was cleared (an account switch) a cancelled first load would leave every /me
  // consumer in the app empty until something refetched.
  const settle = async () => {
    if (queryClient.getQueryData(meKey) !== undefined) {
      await queryClient.cancelQueries({ queryKey: meKey });
    }
  };
  const around = async <T>(request: () => Promise<T>) => {
    await settle();
    const result = await request();
    await settle();
    return result;
  };
  return {
    useEntry: useServerEntry,
    save: async (tableKey, body) => {
      const userSettings = await around(() =>
        client.users.updateTableSettings({ tableKey, requestBody: body }),
      );
      apply(userSettings);
      return findTableSettings(userSettings, tableKey);
    },
    reset: async (tableKey) => {
      const userSettings = await around(() =>
        client.users.deleteTableSettings({ tableKey }),
      );
      apply(userSettings);
    },
  };
};

const TableSettingsAdapterContext = React.createContext<
  TableSettingsAdapter | undefined
>(undefined);

/** Swaps the storage for everything underneath -- tests mount an in-memory adapter here. */
export const TableSettingsAdapterProvider =
  TableSettingsAdapterContext.Provider;

export const useTableSettingsAdapter = (): TableSettingsAdapter => {
  const override = React.useContext(TableSettingsAdapterContext);
  const queryClient = useQueryClient();
  const server = React.useMemo(
    () => createServerTableSettingsAdapter(queryClient),
    [queryClient],
  );
  return override ?? server;
};
